import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TrendingUp, ShieldCheck, Globe2 } from "lucide-react";

// -------------------------------------------------------
// Story Data
// -------------------------------------------------------
// Text fields (tag/company/title/success/improvement) come from
// src/i18n/locales/en.json + de.json under "success.stories" so
// they translate with the site language. image/blockColor are
// presentation-only and stay hardcoded here, matched by array index.

// Cards render at ~600px wide max (aspect-[16/10]) even on desktop, and
// this section is well below the fold. Images are your own uploaded
// photos, pre-cropped to the card's 16:10 aspect and served as WebP —
// each is well under 45 KiB with no visible quality loss at card size.
const storyMedia = [
  {
    image: "/images/success/sap-s4hana.webp",
    imageSmall: "/images/success/sap-s4hana-500.webp",
    blockColor: "#1D4ED8",
  },
  {
    image: "/images/success/sap-sales.webp",
    imageSmall: "/images/success/sap-sales-500.webp",
    blockColor: "#A16207",
  },
  {
    image: "/images/success/supply-chain.webp",
    imageSmall: "/images/success/supply-chain-500.webp",
    blockColor: "#4D7C0F",
  },
  {
    image: "/images/success/data-quality.webp",
    imageSmall: "/images/success/data-quality-500.webp",
    blockColor: "#B54E30",
  },
  {
    image: "/images/success/warehouse-automotive.webp",
    imageSmall: "/images/success/warehouse-automotive-500.webp",
    blockColor: "#92400E",
  },
  {
    image: "/images/success/plant-maintenance.webp",
    imageSmall: "/images/success/plant-maintenance-500.webp",
    blockColor: "#B91C1C",
  },
];

function StoryCard({ story, media, offset, t }) {
  return (
    <div className={`w-full ${offset ? "md:mt-16" : ""}`}>

      {/* Main Card Image */}
      <div className="relative w-full mb-8">

        {/* Offset Color Block */}
        <div
          className="absolute inset-0 translate-x-4 translate-y-4 rounded-sm"
          style={{
            backgroundColor: media.blockColor,
          }}
        />

        {/* Uploaded Image */}
        <div className="relative w-full aspect-[16/10] rounded-sm shadow-lg overflow-hidden bg-white">

          <img
            src={media.image}
            srcSet={`${media.imageSmall} 475w, ${media.image} 700w`}
            sizes="(max-width: 768px) 475px, 700px"
            alt={story?.company ? `${story.company} success story` : "Client success story"}
            width={700}
            height={438}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />

        </div>

        {/* Category Tag */}
        <span
          className="absolute left-4 bottom-[-14px] text-[11px] font-semibold tracking-wide uppercase px-3 py-1 rounded-sm text-white"
          style={{
            backgroundColor: media.blockColor,
          }}
        >
          {story.tag}
        </span>

      </div>

      {/* Card Content */}
      <div className="w-full">

        {/* Title */}
        <h3 className="text-xl md:text-[22px] font-bold text-slate-800 dark:text-white leading-snug mb-2">
           {story.company}
        </h3>

        {/* Company */}
        <p className="text-md font-medium mb-4 text-slate-800 dark:text-slate-400">
           {story.title}
        </p>

        {/* Success Story */}
        <div className="mb-4">
          <p className="text-slate-800 dark:text-slate-300 font-medium text-[15px] leading-relaxed">
            {story.success}
          </p>

        </div>

        {/* Areas of Improvement */}
        <div className="mb-5">
          <p className="text-slate-700 dark:text-slate-500 font-medium text-[15px] leading-relaxed">
            {story.improvement}
          </p>

        </div>

      </div>

    </div>
  );
}

export default function Success() {
  const { t } = useTranslation();

  // Translated text comes from the "success.stories" array; image/color
  // stay local and are matched to the translation by index.
  const stories = t('success.stories', { returnObjects: true });

  return (
    <section className="w-full bg-bg-main py-12 px-6">

      <div className="max-w-6xl mx-auto ">

        <div className="flex justify-center">
          <motion.span
            className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-vizcorn-blue sm:text-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-vizcorn-blue" />
            {t('success.tag')}
          </motion.span>
        </div>

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-20">

          <h2 className="text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-3xl lg:text-4xl mb-5">
            {t('success.heading')}
          </h2>

          <p className="text-slate-700 dark:text-slate-300 text-[16px] leading-relaxed">
            {t('success.subtitle')}
          </p>

          <div className="w-10 h-px bg-border-vz mx-auto mt-6" />

        </div>

        {/* Story Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-20">

          {stories.map((story, i) => (
            <StoryCard
              key={i}
              story={story}
              media={storyMedia[i]}
              offset={i % 2 === 1}
              t={t}
            />
          ))}

        </div>
      </div>

    </section>
  );
}
